"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { API_CONFIG } from "@/config/api.config"
import { toast } from "sonner"
import {
  BookOpen,
  KeyRound,
  ShieldCheck,
  Copy,
  Coins,
  Download,
  QrCode,
  ScanLine,
  History,
  UserCircle,
  ArrowRight,
  CheckCircle2,
  Activity,
} from "lucide-react"

// ============ Types ============
interface ApiEndpoint {
  method: "GET" | "POST"
  path: string
}

interface ApiGroup {
  id: string
  icon: React.ReactNode
  title: string
  what: string
  why: string
  cost: string
  endpoints: ApiEndpoint[]
  detailHref: string
  postmanFile: string
  sampleResponse?: string
}

// ============ Data ============
const API_GROUPS: ApiGroup[] = [
  {
    id: "verify",
    icon: <ScanLine className="w-6 h-6" />,
    title: "ตรวจสอบสลิป (Verify Slip)",
    what: "ตรวจสอบความถูกต้องของสลิปโอนเงินกับข้อมูลจากธนาคารจริง รองรับ 4 วิธี: ข้อมูล QR Code, อัปโหลดไฟล์รูป, รูปแบบ base64 และ URL รูปภาพ",
    why: "ยืนยันว่าลูกค้าโอนเงินจริง ป้องกันสลิปปลอม/สลิปซ้ำ และตรวจเงื่อนไขเพิ่มเติมได้ เช่น บัญชีผู้รับ จำนวนเงิน และวันที่โอน เหมาะกับร้านค้าออนไลน์ที่ต้องยืนยันยอดโอนอัตโนมัติ",
    cost: "1 credit ต่อการตรวจสอบที่สำเร็จ (รายการที่ผิดพลาดไม่หักเครดิต)",
    endpoints: [
      { method: "POST", path: "/api/v3/verify/qr-code" },
      { method: "POST", path: "/api/v3/verify/image" },
      { method: "POST", path: "/api/v3/verify/base64" },
      { method: "POST", path: "/api/v3/verify/url" },
    ],
    detailHref: "/api-connect/endpoint",
    postmanFile: "/postman/verify-slip.postman_collection.json",
  },
  {
    id: "generate",
    icon: <QrCode className="w-6 h-6" />,
    title: "สร้าง QR Code (Generate)",
    what: "สร้าง QR Code สำหรับรับเงิน 2 ประเภท: PromptPay (เบอร์โทร/บัตรประชาชน/e-Wallet) และ Bill Payment (ชำระบิลผ่าน Biller ID) ปรับขนาดและรูปแบบ (png/svg/dataurl) ได้",
    why: "ออก QR รับเงินให้ลูกค้าสแกนจ่ายได้ทันที ระบุจำนวนเงินล่วงหน้าได้ ลดความผิดพลาดจากการกรอกยอดเอง เหมาะกับหน้าชำระเงินและใบแจ้งหนี้",
    cost: "1 credit ต่อการสร้างที่สำเร็จ",
    endpoints: [
      { method: "POST", path: "/api/v3/generate/promptpay" },
      { method: "POST", path: "/api/v3/generate/bill-payment" },
    ],
    detailHref: "/api-connect/endpoint",
    postmanFile: "/postman/generate-qr.postman_collection.json",
  },
  {
    id: "history",
    icon: <History className="w-6 h-6" />,
    title: "ประวัติการใช้งาน (History)",
    what: "ดึงประวัติการตรวจสอบสลิปและการสร้าง QR พร้อม pagination และสถิติสรุป (จำนวนรายการ อัตราสำเร็จ เครดิตที่ใช้)",
    why: "ใช้กระทบยอด ตรวจสอบย้อนหลัง และทำรายงานการใช้งาน API ของระบบคุณเอง",
    cost: "ไม่มีค่าใช้จ่าย (ไม่หักเครดิต)",
    endpoints: [{ method: "GET", path: "/api/v3/history/verifications" }],
    detailHref: "/api-connect/response",
    postmanFile: "/postman/account-history.postman_collection.json",
  },
  {
    id: "account",
    icon: <UserCircle className="w-6 h-6" />,
    title: "ข้อมูลบัญชี (Account Info)",
    what: "ดึงข้อมูลบัญชี แพ็กเกจปัจจุบัน เครดิตคงเหลือ/ทั้งหมด/ที่ใช้ไป และสถานะบัญชี",
    why: "เช็คเครดิตคงเหลือก่อนยิง request จริง และมอนิเตอร์สถานะบัญชีจากระบบของคุณได้",
    cost: "ไม่มีค่าใช้จ่าย (ไม่หักเครดิต)",
    endpoints: [{ method: "GET", path: "/api/v3/account/info" }],
    detailHref: "/api-connect/response",
    postmanFile: "/postman/account-history.postman_collection.json",
  },
  {
    id: "usage",
    icon: <Activity className="w-6 h-6" />,
    title: "สถิติการใช้งาน (Usage)",
    what: "ดึงสถิติการใช้งานตามช่วงเวลา (7/30/90 วัน) ทั้งสรุปยอดรวม สถิติรายวัน แยกตามวิธี และ recentCalls — รายการการเรียกใช้งานจริงแต่ละครั้งว่ามีการเรียก endpoint อะไร ผลเป็นอย่างไร (รองรับ pagination)",
    why: "ใช้มอนิเตอร์ว่าระบบของคุณเรียก API อะไรไปบ้าง ตรวจสอบ request ที่ล้มเหลว วิเคราะห์การใช้เครดิต และทำ dashboard การใช้งานของคุณเอง",
    cost: "ไม่มีค่าใช้จ่าย (ไม่หักเครดิต)",
    endpoints: [{ method: "GET", path: "/api/v3/account/usage" }],
    detailHref: "/api-connect/response",
    postmanFile: "/postman/account-history.postman_collection.json",
    sampleResponse: `{
  "success": true,
  "code": "200999",
  "type": "OPERATION_SUCCESS",
  "message": "Operation completed successfully",
  "data": {
    "usage": {
      "period": "30d",
      "summary": {
        "totalRequests": 128,
        "totalCreditsUsed": 128,
        "successRate": 96.09,
        "averageResponseTime": 742
      },
      "daily": [
        { "date": "2026-09-24", "requests": 12, "credits": 12, "successRate": "100.00" },
        { "date": "2026-09-25", "requests": 20, "credits": 20, "successRate": "95.00" }
      ],
      "byMethod": {
        "qr-code": { "requests": 90, "percentage": "70.3" },
        "image": { "requests": 25, "percentage": "19.5" },
        "promptpay": { "requests": 13, "percentage": "10.2" }
      },
      "topErrors": [
        { "code": "Various errors", "count": 5, "percentage": 100.0 }
      ],
      "recentCalls": [
        {
          "id": "a1b2c3d4-...",
          "requestId": "req_1717997820509_abc123",
          "endpoint": "/api/v3/verify/qr-code",
          "method": "POST",
          "httpStatus": 200,
          "status": "success",
          "processingTimeMs": 680,
          "errorCode": null,
          "errorMessage": null,
          "verificationId": "ver_1717997820509_abc123",
          "generationId": null,
          "createdAt": "2026-09-25T10:31:20.000Z"
        },
        {
          "id": "e5f6g7h8-...",
          "requestId": "req_1717997820510_def456",
          "endpoint": "/api/v3/verify/image",
          "method": "POST",
          "httpStatus": 402,
          "status": "failed",
          "processingTimeMs": 55,
          "errorCode": "402000",
          "errorMessage": "Insufficient credits to process request",
          "verificationId": null,
          "generationId": null,
          "createdAt": "2026-09-25T10:28:04.000Z"
        }
      ],
      "pagination": {
        "currentPage": 1,
        "totalPages": 7,
        "totalItems": 128,
        "itemsPerPage": 20
      }
    },
    "timestamp": "2026-09-26T08:15:00.000Z"
  },
  "meta": {
    "requestId": "req_1717997820509_abc123",
    "timestamp": "2026-09-26T08:15:00.000Z",
    "processingTime": 18
  }
}`,
  },
]

const FULL_POSTMAN_FILE = "/postman/thaislipcheck-api-v3.postman_collection.json"

// ============ Helpers ============
function triggerDownload(fileUrl: string, fileName: string) {
  const link = document.createElement("a")
  link.href = fileUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  toast.success("กำลังดาวน์โหลดไฟล์ Postman collection")
}

// ============ Page ============
export default function ApiConnectGuidePage() {
  const navigate = useNavigate()
  const [copiedUrl, setCopiedUrl] = useState(false)

  const handleCopyBaseUrl = async () => {
    try {
      await navigator.clipboard.writeText(API_CONFIG.BASE_URL)
      setCopiedUrl(true)
      toast.success("คัดลอก Base URL แล้ว")
      setTimeout(() => setCopiedUrl(false), 2000)
    } catch {
      toast.error("คัดลอกไม่สำเร็จ")
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 lg:w-7 lg:h-7 text-brand-blue" />
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">ภาพรวม API (Overview)</h1>
            <p className="text-sm text-gray-500">
              ทำความเข้าใจภาพรวมก่อนไปดูรายละเอียดเชิงลึกที่เมนู API Endpoint และ Response
            </p>
          </div>
        </div>

        {/* Intro / What is this API */}
        <Card className="border border-gray-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4 lg:p-6 space-y-4">
            <h2 className="text-base lg:text-lg font-bold text-gray-900">Thai Slip Check API v3 คืออะไร</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              ชุด REST API สำหรับ <strong>ตรวจสอบสลิปโอนเงิน</strong> และ <strong>สร้าง QR Code รับเงิน</strong> เชื่อมต่อเข้ากับระบบของคุณได้โดยตรง
              ทุก request ยืนยันตัวตนด้วย API Key และคิดค่าใช้งานแบบ credit ต่อรายการ เหมาะกับร้านค้าออนไลน์ ระบบ POS
              และแพลตฟอร์มที่ต้องยืนยันการชำระเงินอัตโนมัติ
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Base URL</p>
                <div className="flex items-center gap-2">
                  <code className="text-blue-600 font-mono text-xs lg:text-sm break-all">{API_CONFIG.BASE_URL}</code>
                  <Button size="sm" variant="ghost" onClick={handleCopyBaseUrl} className="h-6 w-6 p-0 flex-shrink-0" aria-label="คัดลอก Base URL">
                    <Copy className={`w-3.5 h-3.5 ${copiedUrl ? "text-green-600" : ""}`} />
                  </Button>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">เวอร์ชัน</p>
                <Badge className="bg-brand-blue">API v3</Badge>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">การยืนยันตัวตน</p>
                <p className="text-sm text-gray-700 font-medium">API Key (Bearer)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Getting started */}
        <Card className="border border-gray-200">
          <CardContent className="p-4 lg:p-6 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-brand-blue" />
              <h2 className="text-base lg:text-lg font-bold text-gray-900">เริ่มต้นใช้งาน (Quick Start)</h2>
            </div>
            <ol className="space-y-3">
              {[
                { t: "สร้าง / ดู API Key", d: "ไปที่เมนู API Keys เพื่อดูหรือ Reveal key ของคุณ (ดูได้สูงสุด 10 ครั้ง/ชั่วโมง)", action: () => navigate("/api-connect/auth") },
                { t: "(ทางเลือก) ตั้งค่า IP Whitelist", d: "จำกัด IP ที่เรียกใช้ได้สูงสุด 10 IP หากปล่อยว่างจะรับทุก IP" },
                { t: "แนบ API Key ใน Header", d: "ทุก request ต้องมี Authorization: Bearer YOUR_API_KEY" },
                { t: "เรียก API ที่ต้องการ", d: "เลือก endpoint จากการ์ดด้านล่าง หรือดาวน์โหลด Postman collection ไปทดลองยิงได้ทันที" },
                { t: "อ่านผลลัพธ์", d: "ตรวจ field code (รหัส 6 หลัก) และ data — ดูรายละเอียด response ได้ที่เมนู Response" },
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-blue text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{step.t}</p>
                    <p className="text-xs lg:text-sm text-gray-600">{step.d}</p>
                    {step.action && (
                      <Button size="sm" variant="link" onClick={step.action} className="h-auto p-0 text-brand-blue text-xs mt-1">
                        ไปที่ API Keys <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ol>

            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">Header ที่ต้องแนบทุก request</span>
              </div>
              <pre className="text-xs lg:text-sm text-green-400 overflow-x-auto"><code>{`Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`}</code></pre>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Premium ขึ้นไป</Badge>
              <Badge variant="secondary" className="gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> ต้องผ่าน KYC</Badge>
              <Badge variant="secondary" className="gap-1"><Coins className="w-3.5 h-3.5" /> คิดเป็น credit ต่อรายการ</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Download all */}
        <Card className="border border-brand-blue/30 bg-brand-blue/5">
          <CardContent className="p-4 lg:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-brand-blue mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm lg:text-base font-semibold text-gray-900">ดาวน์โหลด Postman Collection (รวมทุก API)</p>
                <p className="text-xs lg:text-sm text-gray-600">
                  import เข้า Postman แล้วตั้งค่าตัวแปร <code className="bg-gray-100 px-1 rounded">apiKey</code> เป็น key ของคุณ ใช้งานได้ทันที
                </p>
              </div>
            </div>
            <Button
              onClick={() => triggerDownload(FULL_POSTMAN_FILE, "thaislipcheck-api-v3.postman_collection.json")}
              className="gap-2 flex-shrink-0"
            >
              <Download className="w-4 h-4" /> ดาวน์โหลดทั้งหมด
            </Button>
          </CardContent>
        </Card>

        {/* API cards */}
        <div>
          <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-3">API แต่ละตัวทำอะไรบ้าง</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {API_GROUPS.map((api) => (
              <Card key={api.id} className="border border-gray-200 flex flex-col">
                <CardContent className="p-4 lg:p-6 flex flex-col flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center flex-shrink-0">
                      {api.icon}
                    </span>
                    <h3 className="text-base font-bold text-gray-900">{api.title}</h3>
                  </div>

                  <div className="space-y-2 text-sm flex-1">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">ทำอะไร</p>
                      <p className="text-gray-700">{api.what}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">เพื่ออะไร</p>
                      <p className="text-gray-700">{api.why}</p>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <Coins className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">ค่าใช้จ่าย: </span>
                        <span className="text-gray-700">{api.cost}</span>
                      </div>
                    </div>
                  </div>

                  {/* Endpoints */}
                  <div className="space-y-1.5">
                    {api.endpoints.map((ep, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1.5">
                        <Badge className={`${ep.method === "GET" ? "bg-green-500" : "bg-blue-500"} text-[10px] px-1.5`}>
                          {ep.method}
                        </Badge>
                        <code className="text-xs font-mono text-blue-600 break-all">{ep.path}</code>
                      </div>
                    ))}
                  </div>

                  {/* Sample response (if provided) */}
                  {api.sampleResponse && (
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-semibold text-brand-blue flex items-center gap-1 select-none">
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                        ตัวอย่าง Response (พร้อมรายการเรียกใช้งาน recentCalls)
                      </summary>
                      <div className="relative bg-gray-900 rounded-lg mt-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(api.sampleResponse || "")
                            toast.success("คัดลอกตัวอย่าง Response แล้ว")
                          }}
                          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white transition-colors"
                          title="Copy"
                          aria-label="คัดลอกตัวอย่าง Response"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <pre className="p-3 pr-9 text-[11px] leading-relaxed text-green-400 overflow-x-auto max-h-96">
                          <code>{api.sampleResponse}</code>
                        </pre>
                      </div>
                    </details>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        triggerDownload(api.postmanFile, api.postmanFile.split("/").pop() || "collection.json")
                      }
                      className="gap-1.5 text-xs"
                    >
                      <Download className="w-3.5 h-3.5" /> Postman
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(api.detailHref)}
                      className="gap-1 text-xs text-brand-blue"
                    >
                      ดูรายละเอียด <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Next steps */}
        <Card className="border border-gray-200">
          <CardContent className="p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-3">ขั้นตอนถัดไป</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => navigate("/api-connect/auth")}
                className="text-left rounded-lg border border-gray-200 p-3 hover:border-brand-blue hover:bg-brand-blue/5 transition-colors"
              >
                <KeyRound className="w-5 h-5 text-brand-blue mb-1" />
                <p className="text-sm font-semibold text-gray-900">API Keys</p>
                <p className="text-xs text-gray-600">จัดการ key และ IP whitelist</p>
              </button>
              <button
                onClick={() => navigate("/api-connect/endpoint")}
                className="text-left rounded-lg border border-gray-200 p-3 hover:border-brand-blue hover:bg-brand-blue/5 transition-colors"
              >
                <ScanLine className="w-5 h-5 text-brand-blue mb-1" />
                <p className="text-sm font-semibold text-gray-900">API Endpoint</p>
                <p className="text-xs text-gray-600">รายละเอียด request ของแต่ละ API</p>
              </button>
              <button
                onClick={() => navigate("/api-connect/response")}
                className="text-left rounded-lg border border-gray-200 p-3 hover:border-brand-blue hover:bg-brand-blue/5 transition-colors"
              >
                <BookOpen className="w-5 h-5 text-brand-blue mb-1" />
                <p className="text-sm font-semibold text-gray-900">Response</p>
                <p className="text-xs text-gray-600">โครงสร้าง response และรหัสสถานะ</p>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
